import React, { useContext, useEffect, useState } from "react";
import { Check, ShieldCheck, Sparkles, CircleCheckBig, ChevronRight } from "lucide-react";

import { Button } from "components/ui/button";
import type { RegisterDraft } from "../types";
import { IntlContext } from "context";

const PROVIDER_DRAFT_STORAGE_KEY = "twoghether:register:provider:draft:v1";
const PROVIDER_STEP_STORAGE_KEY = "twoghether:register:provider:step:v1";

interface Props {
    provider: RegisterDraft | undefined;
    onClearDraft?: () => void;
}

const Welcome: React.FC<Props> = ({ provider, onClearDraft }) => {
    const intl = useContext(IntlContext);
    const [providerSnapshot] = useState(provider);

    useEffect(() => {
        try {
            window.localStorage.removeItem(PROVIDER_DRAFT_STORAGE_KEY);
            window.localStorage.removeItem(PROVIDER_STEP_STORAGE_KEY);
        } catch {
            // ignore storage errors (quota / privacy mode)
        }

        onClearDraft?.();
    }, [onClearDraft]);

    const goToAccount = () => {
        window.location.assign("/connexion");
    };

    return (
        <div className="flex min-h-[520px] items-center justify-center">
            <div className="mx-auto w-full max-w-xl px-2 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
                    <Check className="h-7 w-7" />
                </div>

                <h2 className="mt-6 text-3xl font-black tracking-tight">{intl.formatMessage("authentication.register.label.provider.confirmed")}</h2>
                {providerSnapshot?.has_cesu === "1" && (
                    <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                        {intl.formatMessage("authentication.register.label.service")}
                    </p>
                )}
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                    {intl.formatMessage("authentication.register.label.send_mail")}
                </p>

                <div
                    className={[
                        "mt-10 grid grid-cols-1 justify-items-center gap-8 sm:gap-10",
                        providerSnapshot?.has_cesu === "1"
                            ? "sm:grid-cols-3"
                            : "sm:mx-auto sm:inline-grid sm:grid-cols-2 sm:gap-x-12",
                    ].join(" ")}
                >
                    <div className="flex flex-col items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                            <CircleCheckBig className="h-5 w-5" />
                        </div>
                        <div className="max-w-[220px] text-center text-[11px] font-black uppercase tracking-wider text-foreground">
                            Démarche URSSAF<br/>automatisée
                        </div>
                    </div>
                    {providerSnapshot?.has_cesu === "1" && (
                        <div className="flex flex-col items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-600">
                                <Sparkles className="h-5 w-5" />
                            </div>
                            <div className="max-w-[240px] text-center text-[11px] font-black uppercase tracking-wider text-foreground">
                                Crédit d'impôt immédiat disponible pour vos futurs employeurs
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600">
                            <ShieldCheck className="h-5 w-5" />
                        </div>
                        <div className="max-w-[220px] text-center text-[11px] font-black uppercase tracking-wider text-foreground">
                            Profil activé
                        </div>
                    </div>
                </div>

                <div>
                    <Button onClick={goToAccount} className="mt-10 h-11 font-bold rounded-full px-8 shadow-md shadow-primary/20">
                        {intl.formatMessage("authentication.register.label.access_account")}
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default Welcome;
